import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { clearCredentials, setCredentials } from "../../authSlice";
import toast from "react-hot-toast";
import Select from "react-select";

function AddEditPayment() {
  const { token } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    zimindarId: "",
    amountPaid: "",
    itemsDescription: [],
  });
  const [descriptionInput, setDescriptionInput] = useState("");
  const [allZimidars, setAllZimidars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const dispatch = useDispatch();
  const [tokenExpired, setTokenExpired] = useState(false);

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setDescriptionInput(value);
    setErrors((prevErrors) => ({ ...prevErrors, itemsDescription: "" }));

    setFormData((prevData) => ({
      ...prevData,
      itemsDescription: value.split(/[,|\n]+/).filter(Boolean),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const validateForm = () => {
    let formErrors = {};

    if (!formData.zimindarId) formErrors.zimindarId = "Zimindar is required";
    if (!formData.amountPaid) formErrors.amountPaid = "Amount is required";
    if (formData.itemsDescription.length === 0)
      formErrors.itemsDescription = "Items are required";

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    const url = id
      ? `${process.env.REACT_APP_API_URL}/payments/update?id=${id}`
      : `${process.env.REACT_APP_API_URL}/payments/create`;
    const method = id ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.status === 401) {
        setTokenExpired(true);
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        const data = await response.json();
        if (data.status === 201 || data.status === 200) {
          toast.success(
            id ? "Payment updated successfully" : "Payment added successfully",
            {
              style: {
                border: "1px solid #021F7E",
                padding: "16px",
                color: "#0573E3",
              },
              iconTheme: {
                primary: "#021F7E",
                secondary: "#0573E3",
              },
            }
          );
          console.log(
            id ? "Payment updated successfully" : "Payment added successfully"
          );
          setFormData((prevData) => ({
            ...prevData,
            amountPaid: "",
            itemsDescription: [],
          }));
          setDescriptionInput("");
          // navigate("/dashboard/payment-management");
        } else {
          console.log(data);
        }
      } else {
        const data = await response.json();
        toast.error(data.message, {
          style: {
            border: "1px solid #FE0000",
            padding: "16px",
            color: "#FE0000",
          },
          iconTheme: {
            primary: "#FE0000",
            secondary: "#FE000099",
          },
          duration: 6000,
        });
        console.error("Failed to submit payment:", data);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchPaymentById = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payments/getPaymentById?id=${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
          return;
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
          }
          const paymentData = await response.json();
          if (paymentData && paymentData.status === 200) {
            setFormData({
              zimindarId: paymentData.payment.zimindarId._id || "",
              amountPaid: paymentData.payment.amountPaid || "",
              itemsDescription: paymentData.payment.itemsDescription || [],
            });
            setDescriptionInput(
              paymentData.payment.itemsDescription.join(", ")
            );
          }
        } else {
          console.error("Failed to fetch payment data.");
          navigate("/dashboard/payment-management");
        }
      } catch (error) {
        console.error("Error fetching payment data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchPaymentById();
  }, [id, token, navigate, dispatch]);

  useEffect(() => {
    const fetchZimidars = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/getAllZimindars`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
          return;
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
          }
          const data = await response.json();
          setAllZimidars(data.zimindars || []);
        } else {
          throw new Error("Failed to fetch zimidars.");
        }
      } catch (err) {
        setApiError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (!token || token === "null") {
      dispatch(setCredentials({ token: null, username: null }));
    }
    if (token) fetchZimidars();
  }, [dispatch, token]);

  useEffect(() => {
    if (tokenExpired) {
      dispatch(clearCredentials());
      return;
    }
  }, [dispatch, tokenExpired]);

  if (apiError) return <div>Error: {apiError}</div>;

  const options = allZimidars.map((zimidar) => ({
    value: zimidar._id,
    label: zimidar.name,
  }));

  const handleZimindarChange = (selectedOption) => {
    setFormData((prevData) => ({
      ...prevData,
      zimindarId: selectedOption ? selectedOption.value : "",
    }));
  };

  return (
    <div className="whiteBgDiv UserManagementPadding">
      <div className="mainHeading">
        Sales Management <span>Add/Edit Sales entry</span>
      </div>
      <div className="formHeading UserManagementpage">Enter Sales Details</div>
      <div className="UserManagementGrid">
        <div className="formInputLabelBox">
          <label>Zimindar</label>
          <Select
            name="zimindarId"
            options={options}
            value={
              options.find((opt) => opt.value === formData.zimindarId) || null
            }
            onChange={handleZimindarChange}
            placeholder="Select Zimindar"
            isClearable
            className="formInputBoxSelectContainer"
            classNamePrefix="formInputBoxSelect"
          />
          {/* <select
            name="zimindarId"
            className="form-select"
            value={formData.zimindarId}
            onChange={handleInputChange}
          >
            <option value="">Select Zimindar</option>
            {allZimidars.map((zimidar) => (
              <option key={zimidar._id} value={zimidar._id}>
                {zimidar.name}
              </option>
            ))}
          </select> */}
          {errors.zimindarId && (
            <span className="errorClass">{errors.zimindarId}</span>
          )}
        </div>

        <div className="formInputLabelBox">
          <label>Amount Paid</label>
          <input
            name="amountPaid"
            type="text"
            placeholder="Enter here"
            value={formData.amountPaid}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) handleInputChange(e);
            }}
          />
          {errors.amountPaid && (
            <span className="errorClass">{errors.amountPaid}</span>
          )}
        </div>
      </div>

      <div className="formInputLabelBox">
        <label>Item Description</label>
        <textarea
          rows={5}
          name="itemsDescription"
          placeholder="Enter here"
          value={descriptionInput}
          onChange={handleDescriptionChange}
        />
        {errors.itemsDescription && (
          <span className="errorClass">{errors.itemsDescription}</span>
        )}
      </div>

      <div className="UserManagementBtnFlexDiv">
        {!loading ? (
          <button className="colorBtn" onClick={handleSubmit}>
            Submit
          </button>
        ) : (
          <button className="colorBtn">Loading ...</button>
        )}
        <button
          className="grayBtn"
          onClick={() => navigate("/dashboard/payment-management")}
        >
          Back
        </button>
      </div>
    </div>
  );
}

export default AddEditPayment;
